import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Receipt as TransactionsIcon,
  Map as MapIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  EmojiEvents as RewardsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { getTokenExpiryDate } from '../utils/jwtUtils';
import { setProfile, setProfileLoading, setProfileError } from '../store/profileSlice';
import { apolloClient } from '../apollo';
import { FetchMyProfile } from '../queries/profile';
import { Profile } from '../models/profile';

const drawerWidth = 240;

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

const menuItems: MenuItem[] = [
  { text: 'Transactions', icon: <TransactionsIcon />, path: '/transactions' },
  { text: 'Map', icon: <MapIcon />, path: '/map' },
  { text: 'Rewards', icon: <RewardsIcon />, path: '/rewards' },
  { text: 'Profil', icon: <PersonIcon />, path: '/profile' },
];

interface LayoutProps {
  toggleTheme: () => void;
  mode: 'light' | 'dark';
}

const Layout: React.FC<LayoutProps> = ({ toggleTheme, mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { email, token } = useAppSelector((state) => state.auth);
  const profile = useAppSelector((state) => state.profile.profile);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Fetch profile when token exists but profile is not loaded
  useEffect(() => {
    if (!token || profile) return;

    const fetchProfile = async () => {
      dispatch(setProfileLoading(true));
      try {
        const { data } = await apolloClient.query({
          query: FetchMyProfile,
        });
        dispatch(setProfile(Profile.build((data as any).result.profile)));
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        dispatch(setProfileError(err.message || 'Failed to load profile'));
      }
    };

    fetchProfile();
  }, [token, profile, dispatch]);

  // Calculate time remaining until token expires
  useEffect(() => {
    if (!token) return;

    const updateTimeRemaining = () => {
      const expiryDate = getTokenExpiryDate(token);
      if (!expiryDate) {
        setTimeRemaining('Invalid token');
        return;
      }

      const now = new Date();
      const diff = expiryDate.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      if (minutes > 60) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        setTimeRemaining(`${hours}h ${remainingMinutes}m`);
      } else {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [token]);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Supplier Web - User Impersonation
          </Typography>
          <IconButton color="inherit" onClick={toggleTheme}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Token Expiry Timer */}
          {timeRemaining && (
            <Box sx={{ p: 2, pb: 1 }}>
              <Chip
                icon={<TimeIcon />}
                label={`Token expires: ${timeRemaining}`}
                size="small"
                color={timeRemaining === 'Expired' ? 'error' : timeRemaining.includes('m') && !timeRemaining.includes('h') && parseInt(timeRemaining) < 5 ? 'warning' : 'default'}
                sx={{ width: '100%' }}
              />
            </Box>
          )}
          
          {/* User Info */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                Impersonated User
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {email || 'No email'}
              </Typography>
            </Box>
          </Box>
          <Divider />

          {/* Navigation Menu */}
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
